import React, { useState, useEffect, useCallback } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Save, Send, RotateCcw } from 'lucide-react'
import { Button } from '../ui'
import { WorkerPool } from './WorkerPool'
import { TimesheetTable } from './TimesheetTable'
import { SubmissionModal } from './SubmissionModal'
import { useThemeTokens } from '../../themes'
import { useTimesheetState } from '../../lib/timesheet-utils'
import { getTimesheetToday, submitTimesheet, saveDraft, getToken } from '../../lib/api'
import { prepareTimesheetForSubmission } from '../../lib/submission-validation'
import type { Worker } from '../../types'
import type { DailyTimesheet } from '../../contracts/timesheet'

interface TimesheetBuilderProps {
  onSubmitSuccess?: (timesheet: DailyTimesheet) => void
  className?: string
}

// Custom hook for responsive grid (following OrderCard pattern)
const useResponsiveGrid = () => {
  const [isMobile, setIsMobile] = React.useState(false)
  
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  return isMobile
}

export const TimesheetBuilder: React.FC<TimesheetBuilderProps> = ({
  onSubmitSuccess,
  className
}) => {
  const theme = useThemeTokens()
  const isMobile = useResponsiveGrid()
  const {
    timesheet,
    setTimesheet,
    loading,
    setLoading,
    error,
    setError,
    totalHours,
    totalWorkers,
    addWorkerToTimesheet,
    removeWorkerFromTimesheet,
    updateWorkerTime
  } = useTimesheetState()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false)

  const loadTodaysTimesheet = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const token = getToken()
      if (!token) {
        throw new Error('Authentication required')
      }
      
      const todaysTimesheet = await getTimesheetToday(token)
      setTimesheet(todaysTimesheet)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load timesheet')
    } finally {
      setLoading(false)
    }
  }, [setTimesheet, setLoading, setError])

  // Load today's timesheet on mount
  useEffect(() => {
    loadTodaysTimesheet()
  }, [loadTodaysTimesheet])

  const handleWorkerAdd = (worker: Worker) => {
    if (!timesheet) return
    
    // Check if worker is already on timesheet
    const existingEntry = timesheet.entries.find(entry => entry.workerId === worker.id)
    if (existingEntry) {
      setError(`${worker.name} is already on today's timesheet`)
      return
    }
    
    addWorkerToTimesheet(worker)
    setError(null)
  }

  const handleWorkerRemove = (workerId: number) => {
    removeWorkerFromTimesheet(workerId)
  }

  const handleTimeChange = (workerId: number, timeIn: string, timeOut: string) => {
    updateWorkerTime(workerId, timeIn, timeOut)
  }

  const handleBulkTimeChange = (workerIds: number[], timeIn: string, timeOut: string) => {
    workerIds.forEach(workerId => {
      updateWorkerTime(workerId, timeIn, timeOut)
    })
  }

  // Handle draft saving (existing functionality)
  const handleSaveDraft = async () => {
    if (!timesheet) return
    
    setIsSavingDraft(true)
    setSubmitError(null)
    
    try {
      const token = getToken()
      if (!token) {
        throw new Error('Authentication required')
      }
      
      // Save as draft using the dedicated saveDraft function
      const savedTimesheet = await saveDraft(token, timesheet)
      setTimesheet(savedTimesheet)
      
      // Show success feedback for draft save
      setError(null)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save draft')
    } finally {
      setIsSavingDraft(false)
    }
  }

  // Handle submission modal opening
  const handleOpenSubmissionModal = () => {
    if (!timesheet || timesheet.entries.length === 0) {
      setSubmitError('Cannot submit an empty timesheet')
      return
    }
    
    setIsSubmissionModalOpen(true)
    setSubmitError(null)
  }

  // Handle actual submission through the modal
  const handleSubmitTimesheet = async (timesheetToSubmit: DailyTimesheet) => {
    setIsSubmitting(true)
    setSubmitError(null)
    
    try {
      const token = getToken()
      if (!token) {
        throw new Error('Authentication required')
      }
      
      // Prepare timesheet for submission with calculated fields
      const preparedTimesheet = prepareTimesheetForSubmission(timesheetToSubmit)
      
      // Submit the timesheet
      const submittedTimesheet = await submitTimesheet(token, preparedTimesheet)
      setTimesheet(submittedTimesheet)
      onSubmitSuccess?.(submittedTimesheet)
      
      // Close modal on success
      setIsSubmissionModalOpen(false)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit timesheet')
      throw err // Re-throw to let modal handle error state
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    loadTodaysTimesheet()
    setSubmitError(null)
  }

  // Get worker IDs that are already on the timesheet
  const excludeWorkerIds = timesheet?.entries.map(entry => entry.workerId) || []

  // Check if timesheet is already submitted
  const isSubmitted = timesheet?.status === 'submitted' || timesheet?.status === 'approved'

  // Main container styles
  const containerStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
    gap: theme.spacing.lg,
    padding: theme.spacing.lg,
    minHeight: '600px'
  }

  // Actions bar styles
  const actionsBarStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
    flexWrap: 'wrap',
    gap: theme.spacing.md
  }

  // Status info styles
  const statusInfoStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.lg,
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary
  }

  // Actions buttons styles
  const actionsButtonsStyles: React.CSSProperties = {
    display: 'flex',
    gap: theme.spacing.sm
  }

  // Error display styles
  const errorStyles: React.CSSProperties = {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.error + '15',
    border: `1px solid ${theme.colors.error}`,
    borderRadius: theme.borderRadius.sm,
    color: theme.colors.error,
    fontSize: theme.typography.fontSize.sm,
    marginBottom: theme.spacing.md
  }

  // Submitted banner styles
  const submittedBannerStyles: React.CSSProperties = {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.statusSubmitted + '15',
    border: `1px solid ${theme.colors.statusSubmitted}`,
    borderRadius: theme.borderRadius.sm,
    color: theme.colors.statusSubmitted,
    fontSize: theme.typography.fontSize.sm,
    marginBottom: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm
  }

  // Loading overlay styles
  const loadingOverlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    color: 'white',
    fontSize: theme.typography.fontSize.lg
  }

  if (loading && !timesheet) {
    return (
      <div style={loadingOverlayStyles}>
        Loading today's timesheet...
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={className} data-testid="timesheet-builder">
        {/* Actions Bar */}
        <div style={actionsBarStyles}>
          <div style={statusInfoStyles}>
            <span>Status: {timesheet?.status || 'draft'}</span>
            <span>•</span>
            <span>{totalWorkers} workers</span>
            <span>•</span>
            <span>{totalHours.toFixed(1)} hours</span>
            {isSubmitted && timesheet?.submissionId && (
              <>
                <span>•</span>
                <span>ID: {timesheet.submissionId}</span>
              </>
            )}
          </div>
          
          <div style={actionsButtonsStyles}>
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={loading || isSubmitting || isSavingDraft || isSubmitted}
            >
              <RotateCcw size={16} />
              Reset
            </Button>
            {!isSubmitted && (
              <Button
                variant="ghost"
                onClick={handleSaveDraft}
                disabled={loading || isSubmitting || isSavingDraft || !timesheet || timesheet.entries.length === 0}
              >
                <Save size={16} />
                {isSavingDraft ? 'Saving...' : 'Save Draft'}
              </Button>
            )}
            {!isSubmitted && (
              <Button
                variant="primary"
                onClick={handleOpenSubmissionModal}
                disabled={loading || isSubmitting || isSavingDraft || !timesheet || timesheet.entries.length === 0}
                data-testid="submit-timesheet-btn"
              >
                <Send size={16} />
                Submit Timesheet
              </Button>
            )}
          </div>
        </div>

        {/* Submitted Banner */}
        {isSubmitted && (
          <div style={submittedBannerStyles}>
            ✅ This timesheet has been submitted
            {timesheet?.submittedAt && (
              <span>
                on {new Date(timesheet.submittedAt).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short', 
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </span>
            )}
          </div>
        )}

        {/* Error displays */}
        {error && (
          <div style={errorStyles}>
            {error}
          </div>
        )}
        
        {submitError && (
          <div style={errorStyles}>
            {submitError}
          </div>
        )}

        {/* Main content grid */}
        <div style={containerStyles}>
          {/* Timesheet Table - Left side (now takes 2fr) */}
          {timesheet && (
            <TimesheetTable
              date={timesheet.date}
              entries={timesheet.entries}
              onWorkerAdd={handleWorkerAdd}
              onWorkerRemove={handleWorkerRemove}
              onTimeChange={handleTimeChange}
              onBulkTimeChange={handleBulkTimeChange}
              data-testid="timesheet-table"
            />
          )}

          {/* Worker Pool - Right side (now takes 1fr) */}
          {!isSubmitted && (
            <WorkerPool
              excludeWorkerIds={excludeWorkerIds}
              onWorkerAdd={handleWorkerAdd}
              data-testid="worker-pool"
            />
          )}
        </div>

        {/* Loading overlay for actions */}
        {(loading || isSubmitting || isSavingDraft) && timesheet && (
          <div style={loadingOverlayStyles}>
            {isSubmitting ? 'Submitting timesheet...' : 
             isSavingDraft ? 'Saving draft...' : 
             'Loading...'}
          </div>
        )}

        {/* Submission Modal */}
        <SubmissionModal
          isOpen={isSubmissionModalOpen}
          onClose={() => setIsSubmissionModalOpen(false)}
          timesheet={timesheet}
          onSubmit={handleSubmitTimesheet}
          isSubmitting={isSubmitting}
        />
      </div>
    </DndProvider>
  )
} 